import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ROUTES } from "@/lib/constants";
import { useGettingStarted } from "@/features/gettingStarted/useGettingStarted";
import { setReturnUrl, getFrictionlessOnboarding } from "@/hooks/useAuthPrompt";

/**
 * How long the quest-readiness gate may hold its spinner before failing open.
 *
 * The gate waits on three requests (profile, channels, chat membership). A
 * hung one never errors, so without a ceiling it is not "fail open" at all --
 * it is an endless spinner on every guarded route. Past this, the member gets
 * the page; the avatar ring still shows what's missing, and if the facts do
 * arrive later and a step is missing, the gate redirects to /get-ready then.
 */
export const GATE_TIMEOUT_MS = 12_000;

function AuthGuard() {
  const { isAuthenticated, isLoading, isOnboarded, profileSynced, isAdmin } = useAuth();
  const location = useLocation();

  // Hooks run before any early return. The gate never applies to admins or to
  // a gig sign-up in flight (the next navigation catches the member instead).
  const hasFrictionlessContext = getFrictionlessOnboarding() !== null;
  const onGateExempt =
    location.pathname === ROUTES.GET_READY || location.pathname === ROUTES.ONBOARDING
    || location.pathname === ROUTES.ORG_ONBOARDING;
  const gateApplies = isAuthenticated && isOnboarded && !isAdmin && !hasFrictionlessContext;
  const readiness = useGettingStarted({ enabled: gateApplies && profileSynced });
  const gateWaiting =
    gateApplies && profileSynced && !onGateExempt && readiness.status === "loading";
  const [gateTimedOut, setGateTimedOut] = useState(false);
  useEffect(() => {
    if (!gateWaiting) return;
    const id = setTimeout(() => setGateTimedOut(true), GATE_TIMEOUT_MS);
    // Leaving loading (or the gate) clears the timer and re-arms the next wait.
    return () => {
      clearTimeout(id);
      setGateTimedOut(false);
    };
  }, [gateWaiting]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Record where they were heading before bouncing, or the destination is
    // lost: Home consumes this on the next authenticated render and sends them
    // back. Without it, every guarded deep link — the internal outreach email's
    // /quest/<id>?ref=outreach, a shared link, a bookmark — silently degrades
    // into "you are now on the marketing homepage", and logging in does not
    // recover it.
    //
    // The location comes from the router, NOT from setReturnUrl()'s
    // window.location default: the default is right for a page reacting to its
    // own URL, but here the guard renders before any navigation has happened,
    // and under MemoryRouter window.location does not track the route at all.
    setReturnUrl(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to={ROUTES.HOME} replace />;
  }

  // Wait for server profile sync before deciding onboarding status
  // (localStorage may be empty on a new device)
  if (!profileSynced) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  // Redirect to onboarding if not yet onboarded (unless already on the onboarding page).
  // Skip this redirect when frictionless onboarding is pending — the user clicked
  // "I'm interested" on a quest and we'll auto-onboard them after profile sync.
  if (!isOnboarded && location.pathname !== ROUTES.ONBOARDING
    && location.pathname !== ROUTES.ORG_ONBOARDING && !hasFrictionlessContext) {
    return <Navigate to={ROUTES.ONBOARDING} replace />;
  }

  // Quest-readiness gate: alerts, skills and location are required before
  // using the app. Fails OPEN — a Supabase hiccup must never lock every member
  // out; the avatar ring still shows what's missing. A gig sign-up in flight
  // finishes first; the gate catches the member on the next navigation.
  if (gateApplies && !onGateExempt) {
    if (readiness.status === "loading" && !gateTimedOut) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        </div>
      );
    }
    if (readiness.status === "ready" && readiness.missingEnforced.length > 0) {
      setReturnUrl(`${location.pathname}${location.search}${location.hash}`);
      return <Navigate to={ROUTES.GET_READY} replace />;
    }
  }

  return <Outlet />;
}

export { AuthGuard };
